'use client';

/**
 * API namespace `admin.market.products` (sub-namespace extracted).
 * Verbatim extraction from frontend/src/lib/api-client.ts (originally within admin.market).
 *
 * Part of SPEC-244 IA17 split (refactor/spec244-ia17-api-client).
 * Transformations: T-DEDENT-2 (relative to admin.market), T-EXPORT, T-IMPORTS.
 */

import { apiFetch, apiFetchFormData } from '../core/fetch-client';
import type {
  AdminDisputeListFilters, AdminDisputeListResponse,
  AdminDisputeDetailResponse, AdminResolveDisputeBody,
  AdminResolveAppealBody, AdminModerateReviewBody,
  DisputeResponse, DisputeListResponse, DisputeMessageResponse,
  EvidenceUploadResponse, AppealResponse, ReviewResponse,
  BundleStats, CouponStats, GiftCardStats,
} from '../core/types';
import type {
  AdminProductListItem, AdminProductListResponse,
  ProductListResponse, PublicProductResponse, ProductListFilters,
  Dispute, DisputeStatus, DisputeChannel,
  Evidence, EvidenceType, DisputeMessage,
  Review, ReviewDirection, ReviewRating,
  ReviewListItem, ReviewListResponse,
  ProductBundle, ProductBundleResponse, ProductBundleListResponse,
  ProductBundleStatus, ProductBundleCreateInput, ProductBundleUpdateInput,
  Coupon, CouponResponse, CouponListResponse, CouponStatus,
  CouponPurchaseResponse, CouponPurchaseListResponse,
  CouponCreateInput, CouponUpdateInput, CouponPurchaseInput,
  GiftCard, GiftCardResponse, GiftCardListResponse, GiftCardStatus,
  GiftCardPurchaseResponse, GiftCardPurchaseListResponse,
  GiftCardCreateInput, GiftCardUpdateInput, GiftCardPurchaseInput,
  RedemptionMethod, PurchaseStatus,
  ProductCreateRequest, ProductUpdateRequest,
  SellerProductResponse, ProductStatus,
  Paginated,
} from '../core/shared-types';

export const products = {
  /**
   * GET /api/admin/market/products/pending — list products pending
   * admin approval (status='pending_approval' only).
   *
   * Returns `AdminProductListResponse` (`products: AdminProductListItem[]`
   * + `total` + `page` + `limit`). The backend joins the seller's
   * `storeName` + `storeSlug` inline so the admin can render
   * "Tienda — Producto" without an extra API roundtrip per row.
   *
   * Authz: admin/superadmin (router-level `requireAdminRead`).
   *
   * Spec ref: SPEC-081 Fase 3 §5.3 (GET /admin/market/products/
   *          pending), §4.1 (listPendingProducts service method).
   */
  async listPending(
    page = 0,
    limit = 20,
    signal?: AbortSignal,
  ): Promise<AdminProductListResponse> {
    return apiFetch<AdminProductListResponse>(
      `/admin/market/products/pending?page=${page}&limit=${limit}`,
      { signal },
    );
  },
  /**
   * GET /api/admin/market/products — list ALL products across ALL
   * sellers (any status). Used by the admin moderation page when the
   * admin filters by `status` (e.g., 'rejected', 'approved') — NOT
   * just pending.
   *
   * Returns `AdminProductListResponse` (same shape as `listPending`
   * but filtered server-side by the optional `status` query param).
   *
   * Authz: admin/superadmin (router-level `requireAdminRead`).
   *
   * Spec ref: SPEC-081 Fase 3 §5.3 (GET /admin/market/products), §4.1
   *          (listAllProducts service method).
   */
  async list(
    filters: {
      page?: number;
      limit?: number;
      status?: ProductStatus;
      sellerId?: string;
    },
    signal?: AbortSignal,
  ): Promise<AdminProductListResponse> {
    const qs = new URLSearchParams();
    if (filters.page !== undefined) qs.set('page', String(filters.page));
    if (filters.limit !== undefined) qs.set('limit', String(filters.limit));
    if (filters.status) qs.set('status', filters.status);
    if (filters.sellerId) qs.set('sellerId', filters.sellerId);
    const query = qs.toString();
    return apiFetch<AdminProductListResponse>(
      query ? `/admin/market/products?${query}` : '/admin/market/products',
      { signal },
    );
  },
  /**
   * PUT /api/admin/market/products/:id/approve — approve a pending
   * product (INV-MKT-20). The backend atomically transitions
   * `status: 'pending_approval' → 'approved'` via CAS. The optional
   * `notes` body field (1-500 chars) is recorded in the product's
   * `adminNotes` field (the seller can see it in their dashboard
   * table).
   *
   * 2FA required (INV-SEC-06 §9.6 — per-route `requireRecent2FA`
   * in `routes/admin.route.ts`). On 403 `2FA_REQUIRED`, the caller's
   * catch block re-opens the `TwoFactorFinancialDialog` (rendered
   * by `/admin/market/productos/page.tsx`).
   *
   * Path param: `productId` is the `MarketProduct.productId` (UUID).
   *
   * Spec ref: SPEC-081 Fase 3 §5.3 (PUT /admin/market/products/:id/
   *          approve), §4.1 (approveProduct service method), §4.3
   *          (INV-MKT-20 admin approval gate);
   *          SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6 (INV-SEC-06).
   */
  async approve(
    productId: string,
    options?: { notes?: string },
    signal?: AbortSignal,
  ): Promise<AdminProductListItem> {
    const body =
      options?.notes !== undefined ? { notes: options.notes } : {};
    return apiFetch<AdminProductListItem>(
      `/admin/market/products/${encodeURIComponent(productId)}/approve`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
        signal,
      },
    );
  },
  /**
   * PUT /api/admin/market/products/:id/reject — reject a pending
   * product (INV-MKT-20). The backend atomically transitions
   * `status: 'pending_approval' → 'rejected'` via CAS. The
   * `reason` is MANDATORY (Zod schema — 1-500 chars) — the seller
   * needs to know why their product was rejected so they can fix
   * + resubmit. The reason is recorded in the product's
   * `adminNotes` field (visible to the seller in their dashboard).
   *
   * 2FA required (INV-SEC-06 §9.6 — same rationale as `approve`).
   *
   * Path param: `productId` is the `MarketProduct.productId` (UUID).
   *
   * Spec ref: SPEC-081 Fase 3 §5.3 (PUT /admin/market/products/:id/
   *          reject), §4.1 (rejectProduct service method), §4.3
   *          (INV-MKT-20); SPEC-NEGOCIO §10.4 (INV-MKT-20) + §9.6
   *          (INV-SEC-06).
   */
  async reject(
    productId: string,
    reason: string,
    signal?: AbortSignal,
  ): Promise<AdminProductListItem> {
    return apiFetch<AdminProductListItem>(
      `/admin/market/products/${encodeURIComponent(productId)}/reject`,
      {
        method: 'PUT',
        body: JSON.stringify({ reason }),
        signal,
      },
    );
  },
  // Sprint 139 (Founder directive 2026-09-23) — admin product edit +
  // image management. 3 new methods that allow the admin to edit ANY
  // product's fields + manage its images WITHOUT the sellerId
  // ownership check + WITHOUT status reset (admin edits are trusted).
  /**
   * PUT /api/admin/market/products/:id — admin updates product fields.
   * Does NOT reset status (approved stays approved). 2FA required.
   */
  async updateAsAdmin(
    productId: string,
    data: ProductUpdateRequest,
    signal?: AbortSignal,
  ): Promise<AdminProductListItem> {
    return apiFetch<AdminProductListItem>(
      `/admin/market/products/${encodeURIComponent(productId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
        signal,
      },
    );
  },
  /**
   * POST /api/admin/market/products/:id/images — admin uploads images.
   * Multipart form-data. Same limits as seller (max 3, 5MB, JPG/PNG/WebP).
   * NO 2FA required (non-destructive).
   */
  async uploadImagesAsAdmin(
    productId: string,
    formData: FormData,
    signal?: AbortSignal,
  ): Promise<{ images: string[] }> {
    return apiFetchFormData<{ images: string[] }>(
      `/admin/market/products/${encodeURIComponent(productId)}/images`,
      { method: 'POST', body: formData, signal },
    );
  },
  /**
   * DELETE /api/admin/market/products/:id/images — admin deletes image.
   * Body: { imageUrl }. NO 2FA required (non-destructive).
   */
  async deleteImageAsAdmin(
    productId: string,
    imageUrl: string,
    signal?: AbortSignal,
  ): Promise<{ images: string[] }> {
    return apiFetch<{ images: string[] }>(
      `/admin/market/products/${encodeURIComponent(productId)}/images`,
      { method: 'DELETE', body: JSON.stringify({ imageUrl }), signal },
    );
  },
  /**
   * PR #1024: PATCH /api/admin/market/products/:id/pause
   * Admin hides ANY product from public catalog (approved → paused).
   * Use case: product has inappropriate image, needs to be hidden + corrected.
   * NO 2FA required (non-destructive — product preserved).
   */
  async pauseAsAdmin(
    productId: string,
    signal?: AbortSignal,
  ): Promise<{ productId: string; status: 'paused' }> {
    return apiFetch(
      `/admin/market/products/${encodeURIComponent(productId)}/pause`,
      { method: 'PATCH', signal },
    );
  },
  /**
   * PR #1024: PATCH /api/admin/market/products/:id/unpause
   * Admin reactivates a paused product (paused → approved).
   */
  async unpauseAsAdmin(
    productId: string,
    signal?: AbortSignal,
  ): Promise<{ productId: string; status: 'approved' }> {
    return apiFetch(
      `/admin/market/products/${encodeURIComponent(productId)}/unpause`,
      { method: 'PATCH', signal },
    );
  },
};
